import { useEffect, useState, useMemo } from "react";
import { supabase } from "../lib/supabaseClient";
import LanguageSwitcher from "./LanguageSwitcher";
import { useLanguage } from "./LanguageContext";
import { useUser, PLAN_LABELS } from "./UserContext";
import GeoAuditTool from "./GeoAuditTool";
import OverviewPage from "./OverviewPage";
import QuickStartGuide from "./QuickStartGuide";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');

  .portal-root {
    font-family: 'new-science', 'DM Sans', sans-serif;
    --ink: #0f172a;
    --ink-light: #64748b;
    --surface: #f1f5f9;
    --accent: #2563eb;
    --accent-dark: #1d4ed8;
    --border: #e2e8f0;
    --sidebar-bg: #0f172a;
    --sidebar-hover: #1e293b;
    --sidebar-active: #1e3a5f;
    --white: #ffffff;
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow: hidden;
  }

  /* ===== Top Header ===== */
  .portal-header {
    height: 60px;
    background: var(--white);
    border-bottom: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 24px 0 0;
    flex-shrink: 0;
    z-index: 50;
  }

  .portal-header-left {
    display: flex;
    align-items: center;
    height: 100%;
  }

  /* Logo area in header — exact same width as sidebar */
  .portal-header-logo {
    width: 260px;
    min-width: 260px;
    height: 100%;
    background: var(--sidebar-bg);
    display: flex;
    align-items: center;
    padding: 0 24px;
    flex-shrink: 0;
    box-sizing: border-box;
    border-right: none;
  }

  .portal-logo-text {
    font-family: 'new-science', 'DM Sans', sans-serif;
    font-size: 16px;
    color: white;
    letter-spacing: -0.01em;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .portal-logo-dot {
    width: 6px;
    height: 6px;
    background: var(--accent);
    border-radius: 50%;
  }

  /* portal-header-title removed — brand name now shown in content area */

  /* ===== User area (top right) ===== */
  .portal-user-area {
    display: flex;
    align-items: center;
    gap: 12px;
    position: relative;
  }

  .portal-user-btn {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 10px 6px 6px;
    border-radius: 8px;
    border: 1px solid var(--border);
    background: var(--white);
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
  }

  .portal-user-btn:hover {
    background: var(--surface);
    border-color: #cbd5e1;
  }

  .portal-avatar {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--accent) 0%, #7c3aed 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    color: white;
    letter-spacing: 0.03em;
    flex-shrink: 0;
  }

  .portal-user-name {
    font-size: 13px;
    font-weight: 500;
    color: var(--ink);
    white-space: nowrap;
  }

  .portal-chevron {
    font-size: 10px;
    color: var(--ink-light);
    transition: transform 0.2s;
  }

  .portal-user-btn.open .portal-chevron {
    transform: rotate(180deg);
  }

  /* Dropdown */
  .portal-user-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    width: 220px;
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 10px;
    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
    overflow: hidden;
    z-index: 200;
    animation: dropIn 0.18s cubic-bezier(0.16, 1, 0.3, 1);
  }

  @keyframes dropIn {
    from { opacity: 0; transform: translateY(-6px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .portal-dropdown-header {
    padding: 14px 16px 12px;
    border-bottom: 1px solid var(--border);
  }

  .portal-dropdown-name {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--ink);
    margin-bottom: 2px;
  }

  .portal-dropdown-email {
    font-size: 12px;
    color: var(--ink-light);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .portal-dropdown-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 16px;
    font-size: 13.5px;
    color: var(--ink);
    cursor: pointer;
    transition: background 0.15s;
    border: none;
    background: none;
    width: 100%;
    text-align: left;
    font-family: 'DM Sans', sans-serif;
  }

  .portal-dropdown-item:hover {
    background: var(--surface);
  }

  .portal-dropdown-item.danger {
    color: #ef4444;
  }

  .portal-dropdown-item.danger:hover {
    background: rgba(239, 68, 68, 0.06);
  }

  .portal-dropdown-icon {
    font-size: 15px;
    width: 18px;
    text-align: center;
  }

  .portal-dropdown-divider {
    height: 1px;
    background: var(--border);
    margin: 4px 0;
  }
  .portal-dropdown-info {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 14px;
    font-size: 13px;
    color: #374151;
    cursor: default;
    user-select: none;
  }
  .portal-dropdown-info .portal-dropdown-icon {
    color: #6b7280;
    display: flex;
    align-items: center;
  }

  /* ===== Body (sidebar + content) ===== */
  .portal-body {
    display: flex;
    flex: 1;
    overflow: hidden;
  }

  /* ===== Sidebar ===== */
  .portal-sidebar {
    width: 260px;
    background: var(--sidebar-bg);
    color: white;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    overflow-y: auto;
    padding: 8px 0 0;
  }

  /* Top nav items (Get started, Overview) */
  .portal-nav-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 16px;
    cursor: pointer;
    border-radius: 6px;
    margin: 1px 8px;
    color: #94a3b8;
    font-size: 13.5px;
    font-weight: 500;
    transition: background 0.15s, color 0.15s;
    border: none;
    background: none;
    width: calc(100% - 16px);
    text-align: left;
    font-family: 'new-science', 'DM Sans', sans-serif;
  }

  .portal-nav-item:hover {
    background: var(--sidebar-hover);
    color: #e2e8f0;
  }

  .portal-nav-item.active {
    background: var(--sidebar-active);
    color: white;
  }

  .portal-nav-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    opacity: 0.6;
  }

  .portal-nav-item.active .portal-nav-icon,
  .portal-nav-item:hover .portal-nav-icon {
    opacity: 1;
  }

  /* Brands section header */
  .portal-brands-header {
    display: flex;
    align-items: center;
    padding: 8px 10px 4px 16px;
    margin-top: 12px;
    gap: 4px;
  }

  .portal-brands-label {
    font-size: 11px;
    font-weight: 600;
    color: #475569;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    flex: 1;
  }

  .portal-brands-action {
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    cursor: pointer;
    color: #475569;
    transition: background 0.15s, color 0.15s;
    flex-shrink: 0;
  }

  .portal-brands-action:hover {
    background: #1e293b;
    color: #94a3b8;
  }

  .portal-brands-action svg {
    width: 13px;
    height: 13px;
  }

  /* Brand list items */
  .portal-brand-item {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 7px 16px;
    cursor: pointer;
    border-radius: 6px;
    transition: background 0.15s;
    margin: 1px 8px;
  }

  .portal-brand-item:hover {
    background: var(--sidebar-hover);
  }

  .portal-brand-item.active {
    background: var(--sidebar-active);
  }

  .portal-brand-favicon {
    width: 18px;
    height: 18px;
    border-radius: 4px;
    background: linear-gradient(135deg, #3b82f6, #8b5cf6);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
    color: white;
    flex-shrink: 0;
    text-transform: uppercase;
  }

  .portal-brand-name {
    font-size: 13px;
    color: #94a3b8;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
  }

  .portal-brand-item.active .portal-brand-name {
    color: white;
    font-weight: 500;
  }

  .portal-add-brand {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 16px;
    cursor: pointer;
    margin: 2px 8px;
    border-radius: 6px;
    color: #475569;
    font-size: 12.5px;
    font-family: 'new-science', 'DM Sans', sans-serif;
    transition: background 0.15s, color 0.15s;
    border: none;
    background: none;
    width: calc(100% - 16px);
    text-align: left;
  }

  .portal-add-brand:hover {
    background: #1e293b;
    color: #64748b;
  }

  .portal-brands-collapsed .portal-brand-item {
    display: none;
  }

  .portal-brands-collapsed .portal-add-brand {
    display: none;
  }

  /* Sidebar spacer */
  .portal-sidebar-spacer {
    flex: 1;
  }

  /* Bottom shortcuts */
  .portal-sidebar-bottom {
    padding: 12px 0 16px;
    border-top: 1px solid #1e293b;
  }

  .portal-bottom-item {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 7px 16px;
    cursor: pointer;
    border-radius: 6px;
    margin: 1px 8px;
    color: #64748b;
    font-size: 12.5px;
    font-family: 'new-science', 'DM Sans', sans-serif;
    transition: background 0.15s, color 0.15s;
    border: none;
    background: none;
    width: calc(100% - 16px);
    text-align: left;
  }

  .portal-bottom-item:hover {
    background: #1e293b;
    color: #94a3b8;
  }

  .portal-bottom-item svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    opacity: 0.7;
  }

  /* ===== Brand heading in content ===== */
  .portal-brand-heading {
    font-family: 'new-science', 'DM Sans', sans-serif;
    font-size: 26px;
    font-weight: 600;
    color: var(--ink);
    margin-bottom: 28px;
    letter-spacing: -0.02em;
  }

  /* ===== Main content ===== */
  .portal-content {
    flex: 1;
    overflow-y: auto;
    background: var(--surface);
    padding: 32px;
  }

  /* ===== Wizard overlay ===== */
  .portal-wizard-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.4);
    backdrop-filter: blur(2px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 100;
  }

  .portal-wizard-box {
    background: white;
    padding: 36px;
    width: 600px;
    max-width: 90vw;
    border-radius: 12px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  }

  /* ===== Mobile responsive ===== */
  .portal-hamburger-btn {
    display: none;
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    margin-right: 12px;
  }

  .portal-sidebar-overlay {
    display: none;
  }

  @media (max-width: 768px) {
    .portal-hamburger-btn {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .portal-header-logo {
      width: auto !important;
      min-width: auto !important;
      flex: 0;
      padding: 0 12px !important;
    }

    .portal-logo-text {
      font-size: 14px;
    }

    .portal-sidebar {
      position: fixed;
      left: 0;
      top: 60px;
      height: calc(100vh - 60px);
      width: 260px;
      z-index: 40;
      transform: translateX(-100%);
      transition: transform 0.3s ease;
    }

    .portal-sidebar.open {
      transform: translateX(0);
    }

    .portal-sidebar-overlay {
      display: block;
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.3);
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.3s ease;
      z-index: 35;
    }

    .portal-sidebar-overlay.open {
      opacity: 1;
      pointer-events: auto;
    }

    .portal-body {
      gap: 0 !important;
    }

    .portal-user-name {
      display: none;
    }

    .portal-user-btn {
      padding: 6px !important;
    }
  }
`;

function getInitials(user) {
  const meta = user?.user_metadata;
  if (meta?.first_name) {
    const last = meta.last_name ? meta.last_name[0] : "";
    return (meta.first_name[0] + last).toUpperCase();
  }
  if (meta?.full_name) {
    const parts = meta.full_name.trim().split(" ");
    return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
  }
  return user?.email?.[0]?.toUpperCase() || "?";
}

function getDisplayName(user) {
  const meta = user?.user_metadata;
  if (meta?.first_name) {
    return meta.first_name + (meta.last_name ? ` ${meta.last_name}` : "");
  }
  if (meta?.full_name) return meta.full_name;
  return user?.email?.split("@")[0] || "User";
}

function SidebarFavicon({ url, name }) {
  const [ok, setOk] = useState(true);
  const domain = useMemo(() => { try { return new URL(url).hostname; } catch { return null; } }, [url]);
  const letter = (name?.[0] || "?").toUpperCase();
  if (domain && ok) {
    return (
      <div className="portal-brand-favicon" style={{ background: "none", border: "1px solid rgba(255,255,255,0.1)", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`} alt="" width={14} height={14} onError={() => setOk(false)} style={{ objectFit: "contain" }} />
      </div>
    );
  }
  return <div className="portal-brand-favicon">{letter}</div>;
}

export default function PortalLayout({ session, children, onShowWizard }) {
  const [brands, setBrands] = useState([]);
  const [brandsLoaded, setBrandsLoaded] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [brandsCollapsed, setBrandsCollapsed] = useState(false);
  const [activePage, setActivePage] = useState("brands");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { t } = useLanguage();

  const user = session?.user;
  const initials = getInitials(user);
  const displayName = getDisplayName(user);
  const { plan, credits, auto_updates_paused, setPauseUpdates } = useUser() || {};

  const fetchBrands = async (targetBrandId = null) => {
    const { data } = await supabase
      .from("brands")
      .select("*")
      .eq("user_id", session.user.id)
      .order("created_at", { ascending: true });

    setBrands(data || []);
    setBrandsLoaded(true);
    if (!targetBrandId && (!data || data.length === 0)) setActivePage("quickstart");
    
    if (targetBrandId && data) {
      // After wizard: select the newly created/edited brand and navigate to it
      const target = data.find(b => b.id === targetBrandId);
      if (target) { setSelectedBrand(target); setActivePage("brands"); return; }
    }
    // If current brand was deleted, switch to first available brand
    if (selectedBrand && data && !data.find(b => b.id === selectedBrand.id)) {
      setSelectedBrand(data.length > 0 ? data[0] : null);
    } else if (!selectedBrand && data?.length > 0) {
      setSelectedBrand(data[0]);
    }
  };

  useEffect(() => {
    fetchBrands();
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e) => {
      if (!e.target.closest(".portal-user-area")) setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [dropdownOpen]);

  return (
    <div className="portal-root">
      <style>{styles}</style>

      {/* ===== Top Header ===== */}
      <header className="portal-header">
        <div className="portal-header-left">
          <button className="portal-hamburger-btn" onClick={() => setSidebarOpen(!sidebarOpen)} title="Menu">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
          {/* Logo — matches sidebar width */}
          <div className="portal-header-logo">
            <div className="portal-logo-text">
              <span className="portal-logo-dot" />
              GeoRev
            </div>
          </div>
        </div>

        {/* Language + User — top right */}
        <div style={{display:"flex", alignItems:"center", gap:12}}>
        <LanguageSwitcher />
        <div className="portal-user-area">
          <button
            className={`portal-user-btn ${dropdownOpen ? "open" : ""}`}
            onClick={() => setDropdownOpen(o => !o)}
          >
            <div className="portal-avatar">{initials}</div>
            <span className="portal-user-name">{displayName}</span>
            <span className="portal-chevron">▾</span>
          </button>

          {dropdownOpen && (
            <div className="portal-user-dropdown">
              <div className="portal-dropdown-header">
                <div className="portal-dropdown-name">{displayName}</div>
                <div className="portal-dropdown-email">{user?.email}</div>
              </div>
              {(plan || credits !== null) && (
                <>
                  <div className="portal-dropdown-divider" />
                  {plan && (
                    <div className="portal-dropdown-info">
                      <span className="portal-dropdown-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                      </span>
                      Plan: {PLAN_LABELS[plan] ?? plan}
                    </div>
                  )}
                  {credits !== null && credits !== undefined && (
                    <div className="portal-dropdown-info">
                      <span className="portal-dropdown-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                      </span>
                      Credits: {credits}
                    </div>
                  )}
                </>
              )}
              <div className="portal-dropdown-divider" />
              <div
                className="portal-dropdown-info"
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, cursor: "pointer" }}
                onClick={(e) => { e.stopPropagation(); setPauseUpdates?.(!auto_updates_paused); }}
                title="Stoppt den wöchentlichen automatischen Page-Audit- und KI-Sichtbarkeits-Update für alle Marken in diesem Konto"
              >
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="portal-dropdown-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                  </span>
                  Updates pausieren
                </span>
                <span style={{
                  width: 32, height: 18, borderRadius: 9, position: "relative", flexShrink: 0,
                  background: auto_updates_paused ? "#3b82f6" : "rgba(255,255,255,0.15)",
                  transition: "background 0.15s",
                }}>
                  <span style={{
                    position: "absolute", top: 2, left: auto_updates_paused ? 16 : 2,
                    width: 14, height: 14, borderRadius: "50%", background: "#fff",
                    transition: "left 0.15s",
                  }} />
                </span>
              </div>
              <div className="portal-dropdown-divider" />
              <button
                className="portal-dropdown-item danger"
                onClick={() => { localStorage.removeItem("georev_persist"); supabase.auth.signOut(); }}
              >
                <span className="portal-dropdown-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                </span>
                {t("user.logout")}
              </button>
            </div>
          )}
        </div>
        </div>
      </header>

      {/* Mobile sidebar overlay */}
      <div className={`portal-sidebar-overlay${sidebarOpen ? " open" : ""}`} onClick={() => setSidebarOpen(false)} />

      {/* ===== Body ===== */}
      <div className="portal-body">

        {/* ===== Sidebar ===== */}
        <aside className={`portal-sidebar${sidebarOpen ? " open" : ""}`}>

          {/* Brands section */}
          <div className={`portal-brands-section${brandsCollapsed ? " portal-brands-collapsed" : ""}`}>
            <div className="portal-brands-header">
              <span className="portal-brands-label">
                <svg style={{width:14,height:14,marginRight:6,verticalAlign:"middle",opacity:0.6}} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>
                {t("nav.brands")}
              </span>
              {/* List icon */}
              <span className="portal-brands-action" title="View list">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
              </span>
              {/* Plus icon */}
              <span className="portal-brands-action" title={t("nav.addBrand")} onClick={() => onShowWizard?.()}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              </span>
              {/* Toggle/chevron icon */}
              <span className="portal-brands-action" title="Toggle brands" onClick={() => setBrandsCollapsed(c => !c)} style={{transform: brandsCollapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.2s"}}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              </span>
            </div>

            {brands.map(b => (
              <div
                key={b.id}
                className={`portal-brand-item ${selectedBrand?.id === b.id ? "active" : ""}`}
                onClick={() => { setSelectedBrand(b); setActivePage("brands"); setSidebarOpen(false); }}
              >
                <SidebarFavicon url={b.website_url} name={b.primary_name} />
                <span className="portal-brand-name">{b.primary_name}</span>
              </div>
            ))}

            <button className="portal-add-brand" onClick={() => onShowWizard?.()}>
              <svg style={{width:13,height:13,opacity:0.5}} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              {t("nav.addBrand")}
            </button>
          </div>

          <div className="portal-sidebar-spacer" />

          {/* Bottom shortcuts */}
          <div className="portal-sidebar-bottom">
            <button className="portal-bottom-item" onClick={() => onShowWizard?.()}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></svg>
              {t("dash.sidebarSetup")}
            </button>
            <div style={{borderTop:"1px solid #1e293b", margin:"8px 0 0"}} />
            <div style={{padding:"8px 10px 10px"}}>
              <button
                className="portal-bottom-item"
                onClick={() => window.$crisp && window.$crisp.push(["do", "chat:open"])}
                style={{
                  background: "linear-gradient(135deg, #22c55e, #16a34a)",
                  borderRadius: 8,
                  color: "white",
                  fontWeight: 600,
                  width: "100%",
                  margin: 0,
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                Support
              </button>
            </div>
          </div>

        </aside>

        {/* ===== Content ===== */}
        <main className="portal-content">
          {activePage === "geo-audit" ? (
            <GeoAuditTool session={session} />
          ) : activePage === "quickstart" ? (
            <QuickStartGuide />
          ) : activePage === "overview" ? (
            <OverviewPage
              session={session}
              onNavigateToAudit={() => setActivePage("geo-audit")}
              onNavigateToBrand={(brandId) => {
                if (brandId) {
                  const b = brands.find(br => br.id === brandId);
                  if (b) { setSelectedBrand(b); setActivePage("brands"); }
                }
              }}
              onShowWizard={onShowWizard}
            />
          ) : !brandsLoaded ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#94a3b8", fontSize: 13 }}>
              <div className="dash-spinner" style={{ width: 20, height: 20, borderWidth: 2 }} />
            </div>
          ) : (
            typeof children === "function"
              ? children(selectedBrand, { refreshBrands: fetchBrands })
              : children
          )}
        </main>
      </div>

      {/* ===== Brand Setup Wizard ===== */}
    </div>
  );
}
